import { useFocusRing } from '@react-native-aria/focus'
import clsx from 'clsx'
import type { PropsWithChildren, ReactNode } from 'react'
import { View } from 'react-native'

import { Pressable } from '@/src/elements/pressable'
import type { TFullUserApi } from '@/src/features/user/api/userApi.types'
import { getAvatarUrl } from '@/src/general/utils/getAvatarUrl'
import { dataFocusVisibleClasses } from '@/src/styles/classes'

import { Avatar } from '../../avatar'
import type { TAvatarVariantProps } from '../../avatar/avatar-variants'
import { PlaceholderLine } from '../../placeholder'
import { Typography } from '../../text/Typography'
import type { TTypographyVariantProps } from '../../text/typography-variants'

const WrapperComponent = ({ onPress, children }: PropsWithChildren<{ onPress?: VoidFunction }>): ReactNode => {
  const { isFocusVisible, focusProps } = useFocusRing()
  if (!onPress) return children

  const onPressProps = {
    onPress,
    className: clsx(dataFocusVisibleClasses, 'rounded-sm'),
    dataSet: { 'focus-visible': isFocusVisible },
    ...focusProps,
  }

  return (
    <Pressable {...onPressProps} className="overflow-hidden text-ellipsis whitespace-nowrap">
      {children}
    </Pressable>
  )
}

export type TUserProfilePreviewAvatarProps = {
  onPress?: VoidFunction
  user?: Pick<TFullUserApi, 'name' | 'avatar'>
  className?: string
  style?: {
    icon: Partial<TAvatarVariantProps>
    text: Partial<TTypographyVariantProps>
  }
}
const UserProfileAvatarPreview = ({ user, style, className, onPress }: TUserProfilePreviewAvatarProps): ReactNode => {
  return (
    <WrapperComponent onPress={onPress}>
      <View className={clsx('flex flex-row items-center justify-start gap-3', className)}>
        <Avatar src={getAvatarUrl(user?.avatar)} {...style?.icon} />
        {user?.name ? (
          <Typography numberOfLines={2} className="text-ellipsis" bold {...style?.text}>
            {user?.name}
          </Typography>
        ) : (
          <PlaceholderLine className="h-8 w-32 animate-pulse" />
        )}
      </View>
    </WrapperComponent>
  )
}

const UserProfileTextPreview = ({
  user,
  className,
  onPress,
}: Omit<TUserProfilePreviewAvatarProps, 'style'>): ReactNode => {
  return (
    <WrapperComponent onPress={onPress}>
      {user ? (
        <Typography bold numberOfLines={1} contrast="high" colors="default" className={className}>
          {user.name}
        </Typography>
      ) : (
        <PlaceholderLine className="h-8 w-32 animate-pulse" />
      )}
    </WrapperComponent>
  )
}

export const UserProfilePreview = {
  Avatar: UserProfileAvatarPreview,
  Text: UserProfileTextPreview,
}
